// JOBE-0044.10: usePopoverAnchor — plain-CSS anchored positioning for
// composer popovers. No dependency. Returns a style object suitable
// for an absolutely-positioned (`position: fixed`) element rendered
// into a portal at `document.body`.
//
// `side: 'bottom'` (default) sits below the anchor; flips to `'top'`
// when there's not enough viewport room below.
// `align: 'start'` (default) left-aligns to the anchor; `'end'`
// right-aligns.

import { useLayoutEffect, useRef, useState } from 'react';

const POPOVER_HEIGHT_BUDGET = 360;
const GAP_PX = 6;

// Shallow-equal positioned-style objects. Cheap and tailored to the
// shape we emit (a small fixed set of numeric/string fields).
function sameStyle(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.position !== b.position) return false;
  if (a.visibility !== b.visibility) return false;
  if (a.zIndex !== b.zIndex) return false;
  if (a.top !== b.top) return false;
  if (a.bottom !== b.bottom) return false;
  if (a.left !== b.left) return false;
  if (a.right !== b.right) return false;
  return true;
}

export function usePopoverAnchor(anchorRef, opts = {}) {
  const { side = 'bottom', align = 'start' } = opts;
  const [style, setStyle] = useState(() => ({
    position: 'fixed',
    visibility: 'hidden',
  }));
  // Cache the last-committed style so the scroll/resize listeners can
  // suppress no-op setState calls (LOW-1 from PR #693 review). The
  // typical fixed-anchor-in-fixed-toolbar case re-renders the entire
  // popover tree per scroll frame otherwise.
  const lastStyleRef = useRef(style);

  useLayoutEffect(() => {
    const node = anchorRef && anchorRef.current;
    if (!node) {
      const hidden = { position: 'fixed', visibility: 'hidden' };
      if (!sameStyle(lastStyleRef.current, hidden)) {
        lastStyleRef.current = hidden;
        setStyle(hidden);
      }
      return undefined;
    }
    const recompute = () => {
      const rect = node.getBoundingClientRect();
      const placeBelow = side === 'bottom'
        && (rect.bottom + POPOVER_HEIGHT_BUDGET) < window.innerHeight;
      const next = {
        position: 'fixed',
        zIndex: 50,
        visibility: 'visible',
      };
      if (placeBelow) {
        next.top = Math.round(rect.bottom + GAP_PX);
      } else {
        next.bottom = Math.round(window.innerHeight - rect.top + GAP_PX);
      }
      if (align === 'start') {
        next.left = Math.round(rect.left);
      } else {
        next.right = Math.round(window.innerWidth - rect.right);
      }
      if (sameStyle(lastStyleRef.current, next)) return;
      lastStyleRef.current = next;
      setStyle(next);
    };
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [anchorRef, side, align]);

  return style;
}
